import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  ConversationTypeListResponse,
  CreateConversationTypeRequest,
  CreateOrUpdateConversationTypeRequest,
} from '@clientwire/api';
import { ResponseError, WireApiService } from '@frontend/shared';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class ConversationTypesService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);

  error: WritableSignal<string | undefined> = signal(undefined);

  readonly loading = signal(false);
  readonly conversationTypes: WritableSignal<ConversationTypeListResponse | null> = signal(null);

  loadConversationTypes(): void {
    this.loading.set(true);
    this.#wireApiService.conversationTypesApi
      .getConversationTypes()
      .then((result) => {
        this.conversationTypes.set(result);
        this.loading.set(false);
      })
      .catch(() => {
        this.loading.set(false);
      });
  }

  async createOrUpdateConversationType(conversationType: CreateOrUpdateConversationTypeRequest) {
    try {
      this.clearErrors();
      await this.#wireApiService.conversationTypesApi.createOrUpdateConversationType(
        conversationType
      );
      this.loadConversationTypes();
      this.#toastr.success(
        this.#translateService.instant('SETTINGS.CONVERSATION_TYPES.CONVERSATION_TYPE_UPDATED')
      );
    } catch (error) {
      await this.handleError(error);
    }
  }

  async createConversationType(conversationType: CreateConversationTypeRequest) {
    try {
      this.clearErrors();
      await this.#wireApiService.conversationTypesApi.createConversationType(conversationType);
      this.loadConversationTypes();
      this.#toastr.success(
        this.#translateService.instant('SETTINGS.CONVERSATION_TYPES.CONVERSATION_TYPE_CREATED')
      );
    } catch (error) {
      await this.handleError(error);
    }
  }

  private async handleError(err: any) {
    const jsonResponse = await (err as ResponseError).response?.json();
    const errorMessage = jsonResponse?.message;
    if (errorMessage) {
      this.error.set(errorMessage);
    }
  }

  clearErrors() {
    this.error.set(undefined);
  }
}
